// Available to all components
import Breakpoints from '../utilities/Breakpoints';
import Component from '../site/Component';

/**
 * Internal class for instantiating the Module Manager
 */
export default class ModuleManager {

  /**
   * Create a site
   *
   * @param {object} config - Configuration object for site
   */
  constructor(config, manifest) {
    this.config = config;
    this.manifest = manifest;
    this.manifest.components = this.manifest.components || {};
    this.manifest.componentClasses = this.manifest.componentClasses || {};
    this.manifest.utils = this.manifest.utils || {};

    // Intialize global utility "singletons"
    this.initUtility('breakpoints', Breakpoints);
  }

  /**
   * Start up a component that should only have one instance and add it to window
   *
   * @param {string} componentName - name of component to instantiate
   * @param {string} ComponentClass - component's controller class
   */
  initUtility(componentName, ComponentClass) {
    // Set up the instance
    if (! this.manifest.utils[componentName]) {
      this.manifest.utils[componentName] = new ComponentClass();
    }
  }

  /**
   * Loop through component elements and instantiate the component for each
   *
   * @param {string} componentName - name of component to instantiate
   * @param {string} ComponentClass - component's controller class
   * @param {HTMLElement} context - context on which to initalize components, default is entire document
   */
  initComponent(componentName, ComponentClass, context = document) {
    // Add to array of cached classes
    /* eslint-disable space-unary-ops */
    if (
      ! Object.prototype.hasOwnProperty.call(
          this.manifest.componentClasses, componentName
        )
    ) {
      this.manifest.componentClasses[componentName] = ComponentClass;
    }
    /* eslint-enable */

    // Check if component is configured and extends the core Component class
    if (
      this.config[componentName] &&
      ComponentClass.prototype instanceof Component
    ) {
      this.manifest.components[componentName] = [];
      const component = this.config[componentName];
      const componentEls = context.querySelectorAll(component.selector);

      // Loop through existing component elements in DOM and start up the component
      if (componentEls.length) {
        Array.prototype.forEach.call(componentEls, (element) => {
          // Skip this element if we've already instanced a component on it
          const exists = this.manifest.components[componentName]
            .some((componentInstance) => element === componentInstance.element);

          if (exists) {
            return;
          }

          // Create and start instance
          const instance = new ComponentClass(
            element, component, name, this.manifest
          );
          instance.start();

          // add instance to manifest
          // @todo look into implementing this with a WeakMap
          this.manifest.components[componentName].push({
            instance,
            element,
          });
        });
      }
    } else {
      /* eslint-disable */
      console.error(
        `Component '${componentName}' does not exist or is configured incorrectly.
Check 'client/js/site/config.js' to ensure this component has a configuration object.
Also, verify the '${componentName}' class extends the core component class located in client/js/site/Component.js`
      );
      /* eslint-enable */
    }
  }

  /**
   * Loop through and restart components (use, for example, if you've mremoved components from the DOM and need them re-added)
   *
   * @param {string} componentName - name of component to instantiate
   * @param {HTMLElement} context - context on which to initalize components, default is entire document
   */
  reinitComponent(componentName, context) {
    const componentClass = this.manifest.componentClasses[componentName];

    if (componentClass) {
      this.initComponent(componentName, componentClass, context);
    }
  }

  /**
   * Call a method of a component from a different component
   *
   * @param {string} componentName - Name of component that has target function
   * @param {function} method - Component method you want to call
   * @param {array} args - Array of arguments to pass to component method
   */
  static callComponentMethod(componentName, method, args = []) {
    // Does the component exist?
    if (
      window.mainfest[componentName] &&
      window.mainfest[componentName].length
    ) {
      const componentInstances = window.mainfest[componentName];

      componentInstances.forEach((instance) => {
        // Use JS .apply to call the component method with proper context
        if ('function' === typeof instance[method]) {
          instance[method](...args);
        }
      });
    }
  }
}
